// The real iroh: the WebAssembly build of ../rust, loaded on first use from the versioned runtime
// path (served like the language runtimes). Nothing here runs until a connection needs a relay.

import type { IrohAddress, IrohConnection, IrohEndpoint, IrohStream } from "./types";

/** Set by the build (see assets.ts `irohDefine`): the hash of the Rust sources, and the path the
 * app is served under ("/" normally). */
declare const __IROH_VERSION__: string;
declare const __IROH_BASE__: string;

/** The wasm-bindgen glue's classes (packages/iroh/rust/src/lib.rs). */
interface WasmStream {
  write(data: Uint8Array): Promise<void>;
  read(max: number): Promise<Uint8Array | undefined>;
  finish(): Promise<void>;
  abort(code: number): Promise<void>;
  free(): void;
}
interface WasmConnection {
  remoteId(): string;
  openBi(): Promise<WasmStream>;
  acceptBi(): Promise<WasmStream>;
  close(code: number, reason: string): void;
  closed(): Promise<string>;
  free(): void;
}
interface WasmEndpoint {
  id(): string;
  relayUrl(): string | undefined;
  connect(endpointId: string, relayUrl: string): Promise<WasmConnection>;
  accept(): Promise<WasmConnection | undefined>;
  close(): Promise<void>;
  free(): void;
}
interface WasmModule {
  default(options: { module_or_path: URL }): Promise<unknown>;
  Endpoint: { bind(relayUrls: string[]): Promise<WasmEndpoint> };
}

let loading: Promise<WasmModule> | undefined;

/** Where the runtime lives: `<base>runtimes/iroh/<version>/`. */
export function irohRuntimeUrl(): URL {
  const runtimes = new URL(`${__IROH_BASE__}runtimes/`, location.origin);
  return new URL(`iroh/${__IROH_VERSION__}/`, runtimes);
}

function load(): Promise<WasmModule> {
  loading ??= (async () => {
    const base = irohRuntimeUrl();
    const module = (await import(
      /* @vite-ignore */ new URL("codeshare_iroh.js", base).href
    )) as WasmModule;
    await module.default({ module_or_path: new URL("codeshare_iroh_bg.wasm", base) });
    return module;
  })();
  loading.catch(() => {
    loading = undefined; // try again next time (the network may be back)
  });
  return loading;
}

class Stream implements IrohStream {
  constructor(private readonly inner: WasmStream) {}
  write(data: Uint8Array): Promise<void> {
    return this.inner.write(data);
  }
  read(max: number): Promise<Uint8Array | undefined> {
    return this.inner.read(max);
  }
  finish(): Promise<void> {
    return this.inner.finish();
  }
  abort(code = 0): Promise<void> {
    return this.inner.abort(code);
  }
}

class Connection implements IrohConnection {
  readonly remoteId: string;
  readonly closed: Promise<string>;
  constructor(private readonly inner: WasmConnection) {
    this.remoteId = inner.remoteId();
    this.closed = inner.closed().catch(() => "closed");
  }
  async openStream(): Promise<IrohStream> {
    return new Stream(await this.inner.openBi());
  }
  async acceptStream(): Promise<IrohStream> {
    return new Stream(await this.inner.acceptBi());
  }
  close(code = 0, reason = ""): void {
    this.inner.close(code, reason);
  }
}

class Endpoint implements IrohEndpoint {
  readonly id: string;
  readonly relayUrl: string;
  constructor(private readonly inner: WasmEndpoint) {
    this.id = inner.id();
    this.relayUrl = inner.relayUrl() ?? "";
  }
  async connect(address: IrohAddress): Promise<IrohConnection> {
    return new Connection(await this.inner.connect(address.endpointId, address.relayUrl));
  }
  async accept(): Promise<IrohConnection | undefined> {
    const conn = await this.inner.accept();
    return conn ? new Connection(conn) : undefined;
  }
  close(): Promise<void> {
    return this.inner.close();
  }
}

/** Loads iroh (once) and binds an endpoint on one of the relays. */
export async function bindIroh(relayUrls: string[]): Promise<IrohEndpoint> {
  const module = await load();
  return new Endpoint(await module.Endpoint.bind(relayUrls));
}
